import { z } from 'zod'
import { tolerantArray } from '@/schemas/shared/tolerantArray'
import { OptionalTextSchema } from '../shared/text'

/**
 * Schemas do endereço do cliente (perfil, carrinho e checkout importam daqui).
 *
 * Contrato da API (Glofi.API/Controllers/CustomerController.cs e CustomerService.SerializeAddressDTO), travado em
 * tests/Glofi.API.Tests/Snapshots/Customers/{get-addresses,create-address,update}.shape.txt:
 *
 * | Rota | Verbo | Corpo | Resposta 200 |
 * | --- | --- | --- | --- |
 * | `customer/get-addresses` | GET | - | `IAddress[]` |
 * | `customer/create-address` | POST | `ICreateAddressRequest` | `{ messsage, addresses }` (três "s") |
 * | `customer/update-address` | PUT | `IUpdateAddressRequest` | `{ messsage, addresses }` (três "s") |
 * | `customer/delete-address` | DELETE (corpo em `data`) | `{ id }` | `{ message, addresses }` |
 * | `checkout/get-address-by-cep/{cep}` | GET | - | corpo do serviço de CEP (`IZipCodeAddress`) |
 *
 * `addresses` é sempre a lista completa do cliente depois da alteração: é ela que vai para a store.
 * Endereço principal: `defaultAddress: true` no create/update desmarca os outros no servidor.
 */

const optionalText = OptionalTextSchema

// ===== CEP (regra única da loja) =====

/**
 * CEP com ou sem hífen (`00000-000` ou `00000000`). Regra única: o formulário de endereço, a consulta de CEP, o frete
 * da PDP, o do carrinho e o checkout validam por aqui (o customer é o dono do endereço).
 */
export const ZIP_CODE_REGEX = /^\d{5}-?\d{3}$/

/** CEP como a API consulta e grava no carrinho: só os dígitos. */
export const normalizeZipCode = (zipCode: string): string => zipCode.replace(/\D/g, '')

/** Campo de CEP para a API: tira os espaços das pontas, valida pelo `ZIP_CODE_REGEX` e devolve só os dígitos. */
export const ZipCodeSchema = (message = 'CEP inválido') =>
    z.string().trim().regex(ZIP_CODE_REGEX, message).transform(normalizeZipCode)

// ===== RESPOSTA =====

/** Endereço salvo do cliente. `id` é obrigatório: é por ele que a tela edita, exclui e o checkout troca o endereço. */
export const AddressSchema = z.object({
    id: z.string(),
    name: optionalText,
    street: optionalText,
    number: optionalText,
    complement: optionalText,
    district: optionalText,
    city: optionalText,
    /** Como o cliente digitou (hoje com máscara 00000-000); a API não normaliza. */
    zipCode: optionalText,
    /** Sigla da UF. */
    state: optionalText,
    defaultAddress: z
        .boolean()
        .nullish()
        .transform((value) => value ?? false),
})

/**
 * Lista de endereços (`customer/get-addresses`, as respostas das alterações e o `addresses` do customer, que vem no
 * login). Endereço inválido sai da lista (e é registrado): nunca derruba o login nem a tela de endereços.
 */
export const AddressListSchema = tolerantArray(AddressSchema, 'endereços do cliente')

/**
 * Resposta de create-address, update-address (`messsage`, com três "s", erro de digitação da API) e delete-address
 * (`message`). As duas chaves são opcionais: só `addresses` importa.
 */
export const AddressesResponseSchema = z.object({
    message: z.string().nullish(),
    messsage: z.string().nullish(),
    addresses: AddressListSchema,
})

// ===== REQUISIÇÃO =====

/** Campos que o cliente preenche (os mesmos no create e no update). */
const AddressFieldsSchema = z.object({
    name: z.string(),
    street: z.string(),
    number: z.string(),
    complement: z.string().default(''),
    district: z.string(),
    city: z.string(),
    zipCode: z.string(),
    state: z.string(),
    defaultAddress: z.boolean().default(false),
})

/** `customer/create-address`. `Country` é obrigatório no request da API (o servidor grava "Brasil" de qualquer jeito). */
export const CreateAddressRequestSchema = AddressFieldsSchema.extend({
    country: z.literal('Brasil').default('Brasil'),
})

/** `customer/update-address`: os mesmos campos e o `id` do endereço. */
export const UpdateAddressRequestSchema = CreateAddressRequestSchema.extend({
    id: z.string().min(1),
})

/** `customer/delete-address` (DELETE com o corpo em `data`). */
export const DeleteAddressRequestSchema = z.object({
    id: z.string().min(1),
})

// ===== CONSULTA DE CEP =====

/**
 * `checkout/get-address-by-cep/{cep}`: a API busca em https://api.unigloves.com.br/api/cep/{cep} e devolve o corpo
 * como texto (`Ok(string)`); o cliente HTTP normalmente já converte o JSON, mas o schema aceita o texto também.
 * CEP inexistente: 400 `{ statusError: "zipCodeError", message: "O CEP informado é inválido" }`.
 */
export const ZipCodeAddressSchema = z.preprocess(
    (value) => {
        if (typeof value !== 'string') return value
        try {
            return JSON.parse(value)
        } catch {
            return value
        }
    },
    z.looseObject({
        zipcode: z.string().nullish(),
        street: optionalText,
        district: optionalText,
        city: optionalText,
        complement: optionalText,
        /** Sigla da UF (o campo `state` do serviço vem nulo). É o que vai no `state` do endereço. */
        stateShortname: optionalText,
    }),
)

// ===== TIPOS =====

export type IAddress = z.infer<typeof AddressSchema>
export type IAddressesResponse = z.infer<typeof AddressesResponseSchema>
export type ICreateAddressRequest = z.input<typeof CreateAddressRequestSchema>
export type IUpdateAddressRequest = z.input<typeof UpdateAddressRequestSchema>
export type IDeleteAddressRequest = z.infer<typeof DeleteAddressRequestSchema>
export type IZipCodeAddress = z.infer<typeof ZipCodeAddressSchema>
